import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'video-script');

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const categoryArticles = getArticlesByCategory(activeSlug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug]);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const collectionSchema = currentCategory ? {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 17起草视频`,
    description: currentCategory.description,
    url: `https://17draft.com/category/${currentCategory.slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://17draft.com/article/${article.id}`,
        name: article.title,
      })),
    },
  } : null;

  return (
    <>
      {collectionSchema && (
        <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>
      )}
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 md:pt-24">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 pb-16 md:pb-20">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: currentCategory?.name || '分类' },
                ]}
              />
            </div>

            <div className="mb-8 md:mb-10">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                {currentCategory?.name}
              </h1>
              <p className="text-sm md:text-base text-foreground-600">
                {currentCategory?.description}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-8 md:mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => {
                    setActiveSlug(cat.slug);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group rounded-xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        title={`${article.title} - 17起草视频`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium">
                          {article.categoryName}
                        </span>
                      </div>
                    </div>
                    <div className="p-4 md:p-5">
                      <h2 className="text-sm md:text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h2>
                      <p className="text-xs md:text-sm text-foreground-600 mb-3 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span>{article.author}</span>
                        <span>{article.date} · {article.readTime}分钟阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-3xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}